import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { saveAs } from "file-saver";
import DxfLayouts from "./DxfLayouts";
import { millingNameFromFile } from "./millingDefaults";
import { snapImageFrames } from "./snapImageFrames";

const safeFileName = (value) => (value.trim() || "minta").replace(/[<>:"/\\|?*]/g, "_");
const finiteRect = (rect) => Array.isArray(rect) && rect.length === 4 && rect.every(Number.isFinite);
const positiveRect = (rect) => finiteRect(rect) && rect[0] < rect[2] && rect[1] < rect[3];
const rectInside = (inner, outer) => positiveRect(inner) && positiveRect(outer) &&
  inner[0] >= outer[0] && inner[1] >= outer[1] && inner[2] <= outer[2] && inner[3] <= outer[3];

export function validImageRegions(regions, size) {
  if (!size || !regions.length || regions.length > 30) return false;
  const names = regions.map((region) => region.name.trim().toLocaleLowerCase());
  if (names.some((name) => !name) || new Set(names).size !== names.length) return false;
  return regions.every((region) => positiveRect(region.rect) &&
    region.rect[0] >= 0 && region.rect[1] >= 0 && region.rect[2] <= size.w && region.rect[3] <= size.h &&
    Math.round(region.rect[2]) > Math.round(region.rect[0]) && Math.round(region.rect[3]) > Math.round(region.rect[1]) &&
    (region.cutouts || []).length <= 30 && (region.cutouts || []).every((cutout) => rectInside(cutout.rect, region.rect)));
}

function normalRect([sx, sy], [x, y]) {
  return [Math.min(sx, x), Math.min(sy, y), Math.max(sx, x), Math.max(sy, y)];
}

function clampPointToRect([x, y], rect) {
  return [Math.max(rect[0], Math.min(rect[2], x)), Math.max(rect[1], Math.min(rect[3], y))];
}

export default function ImageCropper() {
  const [file, setFile] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const namePrefix = millingNameFromFile(file?.name);
  const defaultPartName = (index) => `${namePrefix ? `${namePrefix}-` : ""}Rész-${index + 1}`;
  const [url, setUrl] = useState("");
  const [size, setSize] = useState(null);
  const [regions, setRegions] = useState([]);
  const [activeRegionId, setActiveRegionId] = useState(null);
  const [mode, setMode] = useState("pan");
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [snapRadius, setSnapRadius] = useState(10);
  const [snapProposal, setSnapProposal] = useState(null);
  useEffect(() => { setSnapProposal(null); }, [regions, file, snapRadius]);
  const [zoom, setZoom] = useState(1);
  const [viewportWidth, setViewportWidth] = useState(600);
  const viewport = useRef(null);
  const zoomAnchor = useRef(null);
  const img = useRef();
  const start = useRef(null);

  useEffect(() => {
    if (!url || !viewport.current) return undefined;
    const measure = () => setViewportWidth(viewport.current?.clientWidth || 600);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure); observer.observe(viewport.current);
    return () => observer.disconnect();
  }, [url]);
  useLayoutEffect(() => {
    if (zoomAnchor.current && viewport.current) {
      const { x, y, ratio } = zoomAnchor.current;
      viewport.current.scrollLeft = x * ratio - viewport.current.clientWidth / 2;
      viewport.current.scrollTop = y * ratio - viewport.current.clientHeight / 2;
      zoomAnchor.current = null;
    }
  }, [zoom]);
  const changeZoom = value => {
    const next = Math.max(1, Math.min(8, value));
    if (viewport.current && next !== zoom) zoomAnchor.current = {
      x: viewport.current.scrollLeft + viewport.current.clientWidth / 2,
      y: viewport.current.scrollTop + viewport.current.clientHeight / 2, ratio: next / zoom
    };
    start.current = null; setDraft(null); setZoom(next);
  };
  const changeMode = next => { start.current = null; setDraft(null); setMode(next); };
  const fitWidth = size ? Math.min(size.w, viewportWidth, 600 * size.w / size.h) : null;

  useEffect(() => {
    if (!file) return undefined;
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  const activeRegion = useMemo(() => regions.find((region) => region.id === activeRegionId) || null,
    [regions, activeRegionId]);

  const point = (event) => {
    const rect = img.current.getBoundingClientRect();
    return [
      Math.round(Math.max(0, Math.min(size.w, (event.clientX - rect.left) * size.w / rect.width))),
      Math.round(Math.max(0, Math.min(size.h, (event.clientY - rect.top) * size.h / rect.height)))
    ];
  };

  const drawingPoint = (event) => {
    const next = point(event);
    return mode === "cutout" && activeRegion ? clampPointToRect(next, activeRegion.rect) : next;
  };

  const updateRegion = (id, updater) => setRegions((rows) => rows.map((region) => region.id === id ? updater(region) : region));

  const updateRectValue = (regionId, cutoutId, index, value) => {
    updateRegion(regionId, (region) => {
      if (!cutoutId) return { ...region, rect: region.rect.map((current, i) => i === index ? value : current) };
      return {
        ...region,
        cutouts: (region.cutouts || []).map((cutout) => cutout.id === cutoutId
          ? { ...cutout, rect: cutout.rect.map((current, i) => i === index ? value : current) }
          : cutout)
      };
    });
  };

  const saveRegion = (region) => {
    if (!validImageRegions([region], size)) {
      setError("A rész vagy valamelyik belső kivágás koordinátái érvénytelenek.");
      return;
    }
    const [rawX1, rawY1, rawX2, rawY2] = region.rect;
    const x1 = Math.round(rawX1), y1 = Math.round(rawY1), x2 = Math.round(rawX2), y2 = Math.round(rawY2);
    const width = x2 - x1, height = y2 - y1;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) { setError("A böngésző nem tudta előkészíteni a képkivágást."); return; }
    context.drawImage(img.current, x1, y1, width, height, 0, 0, width, height);
    (region.cutouts || []).forEach((cutout) => {
      const [cx1, cy1, cx2, cy2] = cutout.rect.map(Math.round);
      context.clearRect(cx1 - x1, cy1 - y1, cx2 - cx1, cy2 - cy1);
    });
    return new Promise(resolve => canvas.toBlob((blob) => {
      if (blob) saveAs(blob, `${safeFileName(region.name)}.png`);
      else setError("A kivágás mentése nem sikerült.");
      resolve();
    }, "image/png"));
  };

  const addDrawnRect = (rect) => {
    if (!positiveRect(rect)) return;
    if (mode === "region") {
      if (regions.length >= 30) return;
      const id = crypto.randomUUID();
      setRegions((rows) => [...rows, { id, name: defaultPartName(rows.length), rect, cutouts: [] }]);
      setActiveRegionId(id);
    } else if (activeRegion && (activeRegion.cutouts || []).length < 30) {
      updateRegion(activeRegion.id, (region) => ({
        ...region,
        cutouts: [...(region.cutouts || []), { id: crypto.randomUUID(), rect }]
      }));
    }
  };

  const rectOverlay = (rect, key, kind, label) => {
    if (!finiteRect(rect)) return null;
    const x = rect[0], y = rect[1], width = Math.max(0, rect[2] - rect[0]), height = Math.max(0, rect[3] - rect[1]);
    const stroke = kind === "cutout" ? "#d97706" : kind === "draft" ? "#e11d48" : "#0891b2";
    const dash = kind === "cutout" ? `${Math.max(size.w / 250, 4)} ${Math.max(size.w / 350, 3)}` : undefined;
    return <g key={key}>
      <rect x={x} y={y} width={width} height={height} fill={`${stroke}22`} stroke={stroke}
        strokeWidth={1} vectorEffect="non-scaling-stroke" strokeDasharray={dash} />
      {label && <text x={x + Math.max(size.w / 300, 3)} y={Math.max(12, y + Math.max(size.w / 80, 14))}
        fill={stroke} fontSize={Math.max(size.w / 55, 12)}>{label}</text>}
    </g>;
  };

  const coordinateInputs = (region, cutout = null) => {
    const rect = cutout ? cutout.rect : region.rect;
    const prefix = cutout ? `${region.name} kivágás` : region.name;
    return <>
      {["X minimum", "Y minimum", "X maximum", "Y maximum"].map((label, index) => <label key={label}>{label}
        <input className="form-control" style={{ width: 140 }} type="number" step="1"
          aria-label={`${prefix} ${label}`} value={Number.isFinite(rect[index]) ? rect[index] : ""}
          onChange={(event) => updateRectValue(region.id, cutout?.id || null, index, event.target.value === "" ? NaN : Number(event.target.value))} />
      </label>)}
    </>;
  };

  return <section className="card p-3 mt-4">
    <h2 className="h4">PNG / JPG darabolása</h2>
    <p>Rajzolj egy vagy több külső részt, majd szükség esetén rajzolj belső kivágásokat. A koordináták az eredeti kép pixelértékei, és kézzel is átírhatók.</p>
    <label>Kép kiválasztása<input className="form-control" type="file" accept="image/png,image/jpeg" onChange={(event) => {
      const next = event.target.files[0]; event.target.value = "";
      if (!next) return;
      if (!["image/png", "image/jpeg"].includes(next.type) || next.size > 50 * 1024 * 1024) {
        setError("Legfeljebb 50 MB-os PNG/JPG szükséges."); return;
      }
      setFile(next); setSize(null); setRegions([]); setActiveRegionId(null); setMode("pan"); setDraft(null); setError("");
      start.current = null; setZoom(1); zoomAnchor.current = null;
      if (viewport.current) { viewport.current.scrollLeft = 0; viewport.current.scrollTop = 0; }
    }} /></label>
    {error && <p role="alert" className="text-danger">{error}</p>}

    {size && <DxfLayouts imageSize={size} regions={regions} namePrefix={namePrefix} canSave={validImageRegions(regions, size)} onChange={next => {
      setRegions(next); setActiveRegionId(next[0]?.id || null); changeMode("pan");
    }} />}

    {size && regions.length > 0 && <div className="border rounded p-2 mt-3">
      <div className="d-flex flex-wrap gap-2 align-items-end">
        <label>Élkeresés távolsága (px)
          <input className="form-control" type="number" min="1" max="100" value={snapRadius}
            onChange={e => { setSnapRadius(Math.max(1, Math.min(100, Number(e.target.value) || 1))); }} />
        </label>
        <button type="button" className="btn btn-outline-primary" disabled={!validImageRegions(regions, size)} onClick={() => {
          setError(""); changeMode("pan");
          try { setSnapProposal(snapImageFrames(img.current, regions, snapRadius)); }
          catch (failure) { setError(failure.message || "Az élkeresés nem sikerült."); }
        }}>Keretek élhez igazítása</button>
      </div>
      <small className="text-muted">A külső keretek és a belső kivágások minden oldalát a közelben található kontrasztos élhez igazítja.
        Sablon betöltése után egyetlen lépésben pontosíthatod az összes részt. A távolság az eredeti kép pixelméretében értendő.</small>
      {snapProposal && <div className="mt-2">
        <p role="status">Az igazítás javaslata pirossal látható. Ellenőrizd a kereteket; ahol nincs egyértelmű él, az oldal helye megmarad.</p>
        <button type="button" className="btn btn-primary me-2" onClick={() => { setRegions(snapProposal); setSnapProposal(null); }}>Igazítás alkalmazása</button>
        <button type="button" className="btn btn-outline-secondary" onClick={() => setSnapProposal(null)}>Igazítás elvetése</button>
      </div>}
    </div>}

    {size && <div className="d-flex flex-wrap gap-2 mt-3">
      <button type="button" className={`btn btn-sm btn-${mode === "region" ? "primary" : "outline-primary"}`}
        onClick={() => changeMode("region")}>Külső rész rajzolása</button>
      <button type="button" className={`btn btn-sm btn-${mode === "cutout" ? "warning" : "outline-warning"}`}
        disabled={!activeRegion || (activeRegion.cutouts || []).length >= 30} onClick={() => changeMode("cutout")}>Belső kivágás rajzolása</button>
      <button type="button" className={`btn btn-sm btn-${mode === "pan" ? "primary" : "outline-primary"}`} onClick={() => changeMode("pan")}>Kép mozgatása</button>
      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={zoom >= 8} onClick={() => changeZoom(zoom * 1.5)}>Kép nagyítása</button>
      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={zoom <= 1} onClick={() => changeZoom(zoom / 1.5)}>Kép kicsinyítése</button>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { changeZoom(1); if (viewport.current) { viewport.current.scrollLeft = 0; viewport.current.scrollTop = 0; } }}>Teljes kép</button>
      <span className="align-self-center">{Math.round(zoom * 100)}%</span>
      {activeRegion && <span className="align-self-center text-muted">Aktív rész: {activeRegion.name}</span>}
    </div>}

    {url && <div ref={viewport} aria-label="Kép nézet" className="mt-3" style={{ width: "100%", maxHeight: 600, overflow: "auto", touchAction: "none", cursor: mode === "pan" ? "grab" : "crosshair" }}
      onPointerDown={(event) => {
        if (!size || event.button !== 0 || (mode === "cutout" && !activeRegion) || (mode === "region" && regions.length >= 30)) return;
        event.preventDefault();
        const startPoint = drawingPoint(event);
        start.current = { point: startPoint, x: event.clientX, y: event.clientY,
          left: viewport.current.scrollLeft, top: viewport.current.scrollTop };
        setDraft(null);
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        if (!start.current || !size) return;
        if (mode === "pan") {
          viewport.current.scrollLeft = start.current.left + start.current.x - event.clientX;
          viewport.current.scrollTop = start.current.top + start.current.y - event.clientY;
          return;
        }
        setDraft(normalRect(start.current.point, drawingPoint(event)));
      }}
      onPointerUp={(event) => {
        if (mode !== "pan" && start.current && Math.hypot(event.clientX - start.current.x, event.clientY - start.current.y) > 3) {
          addDrawnRect(normalRect(start.current.point, drawingPoint(event)));
        }
        start.current = null; setDraft(null);
      }}
      onPointerCancel={() => { start.current = null; setDraft(null); }}>
      <div style={{ position: "relative", width: size ? fitWidth * zoom : "100%" }}>
      <img ref={img} src={url} alt="Kivágandó kép" draggable={false} style={{ display: "block", width: "100%" }}
        onError={() => setError("A kép nem olvasható.")} onLoad={() => {
          const { naturalWidth: w, naturalHeight: h } = img.current;
          if (w * h > 40000000) { setError("Legfeljebb 40 megapixeles képet válassz."); setSize(null); }
          else { setSize({ w, h }); setError(""); }
        }} />
      {size && <svg role="img" aria-label="Kép kézi kijelölés" viewBox={`0 0 ${size.w} ${size.h}`}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
        {regions.map((region) => <React.Fragment key={region.id}>
          {rectOverlay(region.rect, `${region.id}-outer`, "region", region.name)}
          {(region.cutouts || []).map((cutout, index) => rectOverlay(cutout.rect, cutout.id, "cutout", `Kivágás ${index + 1}`))}
        </React.Fragment>)}
        {draft && rectOverlay(draft, "draft", "draft", mode === "cutout" ? "Új kivágás" : "Új rész")}
        {snapProposal?.map(region => <React.Fragment key={`snap-${region.id}`}>
          {rectOverlay(region.rect, `snap-outer-${region.id}`, "draft")}
          {(region.cutouts || []).map(cutout => rectOverlay(cutout.rect, `snap-${cutout.id}`, "draft"))}
        </React.Fragment>)}
      </svg>}
      </div>
    </div>}

    {size && <p className="text-muted mt-2 mb-2">Forrásméret: {size.w} × {size.h} px. A belső kivágások a letöltött PNG-ben átlátszó területek lesznek.</p>}

    {regions.length > 0 && <div className="my-2">
      <button type="button" className="btn btn-primary" disabled={downloadingAll || !validImageRegions(regions, size)} onClick={async () => {
        setDownloadingAll(true); setError("");
        try {
          for (const region of regions) {
            await saveRegion(region);
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } catch { setError("A képek letöltése megszakadt. Próbáld újra."); }
        finally { setDownloadingAll(false); }
      }}>{downloadingAll ? "Letöltések indítása…" : "Összes kép letöltése külön fájlként"}</button>
      <small className="d-block text-muted">Ha a böngésző rákérdez, engedélyezd a több fájl letöltését. A képek PNG-ként készülnek.</small>
    </div>}

    {regions.map((region, regionIndex) => <fieldset key={region.id}
      className={`border rounded p-2 mb-2 ${activeRegionId === region.id ? "border-primary" : ""}`}>
      <div className="d-flex gap-2 flex-wrap align-items-end">
        <label>Rész neve<input className="form-control" aria-label={`${regionIndex + 1}. rész neve`} maxLength={150} value={region.name}
          onChange={(event) => updateRegion(region.id, (current) => ({ ...current, name: event.target.value }))} /></label>
        {coordinateInputs(region)}
        <button type="button" className="btn btn-outline-primary" onClick={() => { setActiveRegionId(region.id); setMode("cutout"); }}>
          Kivágás hozzáadása
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={() => { setActiveRegionId(region.id); setMode("pan"); }}>
          Rész aktiválása
        </button>
        <button type="button" className="btn btn-primary" disabled={!validImageRegions([region], size)} onClick={() => saveRegion(region)}>
          {region.name.trim() || `Rész-${regionIndex + 1}`}.png letöltése
        </button>
        <button type="button" className="btn btn-outline-danger" onClick={() => {
          setRegions((rows) => rows.filter((item) => item.id !== region.id));
          if (activeRegionId === region.id) { setActiveRegionId(null); setMode("pan"); }
        }}>Rész törlése</button>
      </div>

        {(region.cutouts || []).map((cutout, cutoutIndex) => <div key={cutout.id} className="d-flex flex-wrap gap-2 align-items-end mt-2 ms-3 border-start border-warning border-3 ps-2">
            <strong className="align-self-center">Belső kivágás {cutoutIndex + 1}</strong>
            {coordinateInputs(region, cutout)}
            <button type="button" className="btn btn-outline-danger" onClick={() => updateRegion(region.id, (current) => ({
              ...current, cutouts: (current.cutouts || []).filter((item) => item.id !== cutout.id)
            }))}>Kivágás törlése</button>
        </div>)}
    </fieldset>)}

    {regions.length > 0 && !validImageRegions(regions, size) && <p role="alert" className="text-danger">
      A résznevek legyenek különbözők, minden téglalapnak legyen pozitív mérete, és a belső kivágások teljesen a külső részen belül legyenek.
    </p>}
  </section>;
}
